import { Box, Paper, Skeleton, useTheme } from '@mui/material'
import React from 'react'

/*
 * Generic container for system settings skeletons
 */
export const SystemSettingsSkeletonContainer: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
      }}
    >
      {children}
    </Box>
  )
}

/*
 * Skeleton for closed accordion
 */
export const ClosedAccordionSkeleton: React.FC = () => {
  return (
    <Paper sx={{ padding: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
        <Skeleton variant="text" width="20%" />
        <Skeleton variant="circular" width={20} height={20} />
      </Box>
    </Paper>
  )
}

/*
 * Skeleton for tachometer accordion
 */
export const TachoMeterAccordionSkeleton: React.FC<{ rectangleAmount: number }> = ({ rectangleAmount }) => {
  const theme = useTheme()

  return (
    <Paper sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Skeleton variant="text" width="20%" />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: 2,

          [theme.breakpoints.up('md')]: {
            gridTemplateColumns: 'repeat(3, 1fr)',
          },
        }}
      >
        {[...Array(rectangleAmount)].map((_, index) => (
          <Skeleton key={index} variant="rectangular" sx={{ minHeight: 250, height: '100%', width: '100%', borderRadius: 2 }} />
        ))}
      </Box>
    </Paper>
  )
}

/*
 * Generic accordion skeleton with short rows
 */
export const GenericAccordionSkeleton: React.FC<{ rowAmount: number }> = ({ rowAmount }) => {
  return (
    <Paper sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Skeleton variant="text" width="20%" />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[...Array(rowAmount)].map((_, index) => (
          <Skeleton key={index} variant="rectangular" sx={{ minHeight: 60, height: '100%', width: '100%', borderRadius: 2 }} />
        ))}
      </Box>
    </Paper>
  )
}

/*
 * Skeleton for solar settings
 */
export const SolarSettingsSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <ClosedAccordionSkeleton />
      <TachoMeterAccordionSkeleton rectangleAmount={9} />
    </SystemSettingsSkeletonContainer>
  )
}

/*
 * Skeleton for battery settings
 */
export const BatterySettingsSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <ClosedAccordionSkeleton />
      <ClosedAccordionSkeleton />
      <TachoMeterAccordionSkeleton rectangleAmount={3} />
    </SystemSettingsSkeletonContainer>
  )
}

/*
 * Skeleton for power meter settings
 */
export const PowerMeterSettingsSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <ClosedAccordionSkeleton />
      <TachoMeterAccordionSkeleton rectangleAmount={3} />
    </SystemSettingsSkeletonContainer>
  )
}

/*
 * Skeleton for wallbox settings
 */
export const WallboxSettingsSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <TachoMeterAccordionSkeleton rectangleAmount={3} />
      <GenericAccordionSkeleton rowAmount={12} />
      <ClosedAccordionSkeleton />
      <ClosedAccordionSkeleton />
    </SystemSettingsSkeletonContainer>
  )
}

/*
 * Skeleton for smart charge settings
 */
export const SmartChargeSettingsSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <ClosedAccordionSkeleton />
      <GenericAccordionSkeleton rowAmount={4} />
      <ClosedAccordionSkeleton />
      <ClosedAccordionSkeleton />
      <ClosedAccordionSkeleton />
    </SystemSettingsSkeletonContainer>
  )
}

export const TariffSettingsSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <GenericAccordionSkeleton rowAmount={4} />
      <GenericAccordionSkeleton rowAmount={4} />
      <ClosedAccordionSkeleton />
    </SystemSettingsSkeletonContainer>
  )
}

/*
 * Skeleton for smart power settings
 */
export const SmartPowerSettingsSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <ClosedAccordionSkeleton />
    </SystemSettingsSkeletonContainer>
  )
}

/*
 * Skeleton for emergency power settings
 */
export const EmergencyPowerSettingsSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <ClosedAccordionSkeleton />
      <GenericAccordionSkeleton rowAmount={2} />
    </SystemSettingsSkeletonContainer>
  )
}

/*
 * Skeleton for installation settings
 */
export const InstallationSettingsSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <GenericAccordionSkeleton rowAmount={6} />
    </SystemSettingsSkeletonContainer>
  )
}

/*
 * Skeleton for grid and system protection configuration settings
 */
export const GridAndSystemProtectionConfigurationSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <ClosedAccordionSkeleton />
      <GenericAccordionSkeleton rowAmount={2} />
      <ClosedAccordionSkeleton />
      <ClosedAccordionSkeleton />
      <ClosedAccordionSkeleton />
      <ClosedAccordionSkeleton />
      <ClosedAccordionSkeleton />
    </SystemSettingsSkeletonContainer>
  )
}

/*
 * Skeleton for battery settings
 */
export const StorageBatterySettingsSkeleton: React.FC = () => {
  return (
    <SystemSettingsSkeletonContainer>
      <GenericAccordionSkeleton rowAmount={1} />
      <ClosedAccordionSkeleton />
    </SystemSettingsSkeletonContainer>
  )
}
